import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  Lightbulb,
  Languages,
  Loader2,
  Send,
  ShieldCheck,
} from "lucide-react";
import gsLogo from "../../../assets/gs_logo-goktas.svg";
import { createPublicKaizenSuggestion } from "../../../api/kalite/kaizenApi";
import {
  CLASSIFICATION_OPTIONS,
  DEPARTMENT_SUGGESTIONS,
  IMPACT_FREQUENCY_OPTIONS,
} from "./kaizenConstants";
import { KAIZEN_TRANSLATIONS } from "./kaizenTranslations";
import "./Kaizen.css";

const localDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const initialForm = {
  formDate: localDate(),
  firstName: "",
  lastName: "",
  department: "",
  shift: "",
  employeeNo: "",
  teamLeader: "",
  subject: "",
  classification: "",
  currentSituation: "",
  proposedSolution: "",
  impactFrequency: "",
  investment: "",
  expectedBenefit: "",
  targetDate: "",
  responsiblePerson: "",
  implementationStatus: "beklemede",
  sourcePoint: "genel",
  website: "",
};

function FieldError({ id, message }) {
  return message ? (
    <span id={id} className="kaizen-field-error" role="alert">
      <CircleAlert size={18} /> {message}
    </span>
  ) : null;
}

function KaizenPublicForm() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [language, setLanguage] = useState(() => searchParams.get("lang") === "tk" ? "tk" : "tr");
  const t = KAIZEN_TRANSLATIONS[language];
  const sourcePoint = useMemo(
    () => (searchParams.get("nokta") || "genel").trim().slice(0, 80),
    [searchParams]
  );
  const [form, setForm] = useState({ ...initialForm, sourcePoint });
  const [step, setStep] = useState(1);
  const [showResult, setShowResult] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [successNo, setSuccessNo] = useState("");

  const requiredPersonnelFields = [
    ["firstName", t.errors.firstName],
    ["lastName", t.errors.lastName],
    ["department", t.errors.department],
    ["shift", t.errors.shift],
    ["employeeNo", t.errors.employeeNo],
    ["teamLeader", t.errors.teamLeader],
  ];

  const requiredSuggestionFields = [
    ["subject", t.errors.subject],
    ["classification", t.errors.classification],
    ["currentSituation", t.errors.currentSituation],
    ["proposedSolution", t.errors.proposedSolution],
  ];

  useEffect(() => {
    document.documentElement.lang = language === "tk" ? "tk" : "tr";
    document.title = language === "tk" ? "Işgär teklip formasy | Göktaş" : "Çalışan Öneri Formu | Göktaş";
    return () => {
      document.documentElement.lang = "tr";
      document.title = "Göktaş Portal";
    };
  }, [language]);

  const changeLanguage = (nextLanguage) => {
    setLanguage(nextLanguage);
    setErrors({});
    setServerError("");
    const nextParams = new URLSearchParams(searchParams);
    if (nextLanguage === "tk") nextParams.set("lang", "tk");
    else nextParams.delete("lang");
    setSearchParams(nextParams, { replace: true });
  };

  const change = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = (fields) => {
    const nextErrors = {};
    fields.forEach(([name, message]) => {
      if (!String(form[name] || "").trim()) nextErrors[name] = message;
    });
    setErrors(nextErrors);

    const firstInvalidField = Object.keys(nextErrors)[0];
    if (firstInvalidField) {
      window.requestAnimationFrame(() => {
        const element = document.querySelector(`[name="${firstInvalidField}"]`);
        element?.focus();
        element?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
    return !firstInvalidField;
  };

  const goToSuggestion = () => {
    if (!validate(requiredPersonnelFields)) return;
    setErrors({});
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!validate(requiredSuggestionFields)) return;

    setSubmitting(true);
    setServerError("");
    try {
      const response = await createPublicKaizenSuggestion({ ...form, sourcePoint, language });
      setSuccessNo(response.data?.data?.suggestionNo || "");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setServerError(
        language === "tk"
          ? t.requestError
          : error.response?.data?.message || t.requestError
      );
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setForm({ ...initialForm, sourcePoint, formDate: localDate() });
    setErrors({});
    setServerError("");
    setSuccessNo("");
    setShowResult(false);
    setStep(1);
  };

  const hasErrors = Object.values(errors).some(Boolean);

  if (successNo) {
    return (
      <main className="kaizen-public kaizen-success-page">
        <section className="kaizen-success-card" aria-live="polite">
          <div className="kaizen-success-icon"><CheckCircle2 size={54} /></div>
          <span className="kaizen-eyebrow">{t.successEyebrow}</span>
          <h1>{t.successTitle}</h1>
          <p>{t.successText}</p>
          <strong className="kaizen-success-no">{successNo}</strong>
          <p className="kaizen-success-message">{t.successMessage}</p>
          <button type="button" className="kaizen-primary-button" onClick={restart}>
            <Lightbulb size={22} /> {t.newSuggestion}
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="kaizen-public">
      <header className="kaizen-public-header">
        <img src={gsLogo} alt="Göktaş Metal" />
        <div>
          <span>Göktaş Metal</span>
          <strong>{t.headerTitle}</strong>
        </div>
        <span className="kaizen-secure"><ShieldCheck size={19} /> {t.noLogin}</span>
      </header>

      <section className="kaizen-form-shell">
        <div className="kaizen-language-card">
          <div><Languages size={23} /><span>Form dili / Formanyň dili</span></div>
          <div className="kaizen-language-switch" role="group" aria-label="Form dili">
            <button type="button" className={language === "tr" ? "active" : ""} aria-pressed={language === "tr"} onClick={() => changeLanguage("tr")}>Türkçe</button>
            <button type="button" className={language === "tk" ? "active" : ""} aria-pressed={language === "tk"} onClick={() => changeLanguage("tk")}>Türkmençe</button>
          </div>
        </div>
        <div className="kaizen-intro">
          <span className="kaizen-eyebrow"><Lightbulb size={19} /> {t.eyebrow}</span>
          <h1>{t.heroTitle}</h1>
          <p>{t.heroText}</p>
        </div>

        <div className="kaizen-progress-card" aria-label={t.step(step)}>
          <div>
            <strong>{t.step(step)}</strong>
            <span>{step === 1 ? t.stepOneText : t.stepTwoText}</span>
          </div>
          <div className="kaizen-progress-track" aria-hidden="true"><span style={{ width: step === 1 ? "50%" : "100%" }} /></div>
        </div>

        <form className="kaizen-form-card" onSubmit={submit} noValidate>
          <div className="kaizen-card-heading">
            <div>
              <span>{step === 1 ? "1" : "2"}</span>
              <div>
                <h2>{step === 1 ? t.personalTitle : t.suggestionTitle}</h2>
                <p>{step === 1 ? t.requiredHint : t.simpleHint}</p>
              </div>
            </div>
            {step === 1 && <time>{new Intl.DateTimeFormat(language === "tk" ? "tk-TM" : "tr-TR").format(new Date())}</time>}
          </div>

          {hasErrors && <div className="kaizen-error-summary" role="alert"><CircleAlert size={23} /><div><strong>{t.missingTitle}</strong><span>{t.missingText}</span></div></div>}

          {step === 1 ? (
            <>
              <div className="kaizen-form-grid">
                <label className={errors.firstName ? "has-error" : ""}><span>{t.firstName} <b>*</b></span><input name="firstName" value={form.firstName} onChange={change} autoComplete="given-name" placeholder={t.firstNamePlaceholder} aria-invalid={!!errors.firstName} aria-describedby={errors.firstName ? "firstName-error" : undefined} /><FieldError id="firstName-error" message={errors.firstName} /></label>
                <label className={errors.lastName ? "has-error" : ""}><span>{t.lastName} <b>*</b></span><input name="lastName" value={form.lastName} onChange={change} autoComplete="family-name" placeholder={t.lastNamePlaceholder} aria-invalid={!!errors.lastName} aria-describedby={errors.lastName ? "lastName-error" : undefined} /><FieldError id="lastName-error" message={errors.lastName} /></label>
                <label className={errors.employeeNo ? "has-error" : ""}><span>{t.employeeNo} <b>*</b></span><input name="employeeNo" value={form.employeeNo} onChange={change} inputMode="numeric" placeholder={t.employeeNoPlaceholder} aria-invalid={!!errors.employeeNo} aria-describedby={errors.employeeNo ? "employeeNo-error" : "employeeNo-help"} /><small id="employeeNo-help" className="kaizen-field-help">{t.employeeNoHelp}</small><FieldError id="employeeNo-error" message={errors.employeeNo} /></label>
                <label className={errors.department ? "has-error" : ""}><span>{t.department} <b>*</b></span><select name="department" value={form.department} onChange={change} aria-invalid={!!errors.department} aria-describedby={errors.department ? "department-error" : undefined}><option value="">{t.departmentPlaceholder}</option>{DEPARTMENT_SUGGESTIONS.map((item) => <option value={item} key={item}>{t.departments[item] || item}</option>)}</select><FieldError id="department-error" message={errors.department} /></label>
                <label className={errors.shift ? "has-error" : ""}><span>{t.shift} <b>*</b></span><select name="shift" value={form.shift} onChange={change} aria-invalid={!!errors.shift} aria-describedby={errors.shift ? "shift-error" : undefined}><option value="">{t.shiftPlaceholder}</option>{Object.entries(t.shifts).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select><FieldError id="shift-error" message={errors.shift} /></label>
                <label className={errors.teamLeader ? "has-error" : ""}><span>{t.teamLeader} <b>*</b></span><input name="teamLeader" value={form.teamLeader} onChange={change} placeholder={t.teamLeaderPlaceholder} aria-invalid={!!errors.teamLeader} aria-describedby={errors.teamLeader ? "teamLeader-error" : undefined} /><FieldError id="teamLeader-error" message={errors.teamLeader} /></label>
              </div>
              <div className="kaizen-form-actions end"><button type="button" className="kaizen-primary-button" onClick={goToSuggestion}>{t.continueButton} <ArrowRight size={23} /></button></div>
            </>
          ) : (
            <>
              <div className="kaizen-form-grid">
                <label className={errors.subject ? "has-error" : ""}><span>{t.subject} <b>*</b></span><input name="subject" value={form.subject} onChange={change} placeholder={t.subjectPlaceholder} maxLength={160} aria-invalid={!!errors.subject} aria-describedby={errors.subject ? "subject-error" : undefined} /><FieldError id="subject-error" message={errors.subject} /></label>
                <label className={errors.classification ? "has-error" : ""}><span>{t.classification} <b>*</b></span><select name="classification" value={form.classification} onChange={change} aria-invalid={!!errors.classification} aria-describedby={errors.classification ? "classification-error" : undefined}><option value="">{t.classificationPlaceholder}</option>{CLASSIFICATION_OPTIONS.map((item) => <option key={item.value} value={item.value}>{t.classifications[item.value] || item.label}</option>)}</select><FieldError id="classification-error" message={errors.classification} /></label>
                <label className={errors.currentSituation ? "has-error" : ""}><span>{t.currentSituation} <b>*</b></span><small className="kaizen-field-help">{t.currentSituationHelp}</small><textarea name="currentSituation" value={form.currentSituation} onChange={change} rows="5" placeholder={t.currentSituationPlaceholder} maxLength={2000} aria-invalid={!!errors.currentSituation} aria-describedby={errors.currentSituation ? "currentSituation-error" : undefined} /><small className="kaizen-char-count">{form.currentSituation.length}/2000</small><FieldError id="currentSituation-error" message={errors.currentSituation} /></label>
                <label className={errors.proposedSolution ? "has-error" : ""}><span>{t.proposedSolution} <b>*</b></span><small className="kaizen-field-help">{t.proposedSolutionHelp}</small><textarea name="proposedSolution" value={form.proposedSolution} onChange={change} rows="5" placeholder={t.proposedSolutionPlaceholder} maxLength={2000} aria-invalid={!!errors.proposedSolution} aria-describedby={errors.proposedSolution ? "proposedSolution-error" : undefined} /><small className="kaizen-char-count">{form.proposedSolution.length}/2000</small><FieldError id="proposedSolution-error" message={errors.proposedSolution} /></label>
              </div>

              <button type="button" className="kaizen-optional-toggle" onClick={() => setShowResult((value) => !value)} aria-expanded={showResult}><span><strong>{t.optionalTitle}</strong><small>{t.optionalText}</small></span>{showResult ? <ChevronUp size={25} /> : <ChevronDown size={25} />}</button>

              {showResult && <div className="kaizen-optional-panel kaizen-form-grid"><label><span>{t.impactFrequency}</span><select name="impactFrequency" value={form.impactFrequency} onChange={change}>{IMPACT_FREQUENCY_OPTIONS.map((item) => <option key={item.value} value={item.value}>{t.impactOptions[item.value] || item.label}</option>)}</select></label><label><span>{t.implementationStatus}</span><select name="implementationStatus" value={form.implementationStatus} onChange={change}><option value="beklemede">{t.notImplemented}</option><option value="yapildi">{t.implemented}</option></select></label><label><span>{t.investment}</span><input name="investment" value={form.investment} onChange={change} placeholder={t.investmentPlaceholder} /></label><label><span>{t.expectedBenefit}</span><input name="expectedBenefit" value={form.expectedBenefit} onChange={change} placeholder={t.expectedBenefitPlaceholder} /></label><label><span>{t.targetDate}</span><input type="date" name="targetDate" value={form.targetDate} onChange={change} /></label><label><span>{t.responsiblePerson}</span><input name="responsiblePerson" value={form.responsiblePerson} onChange={change} placeholder={t.responsiblePlaceholder} /></label></div>}

              <input type="text" hidden name="website" value={form.website} onChange={change} tabIndex="-1" autoComplete="off" aria-hidden="true" />
              {serverError && <div className="kaizen-submit-error" role="alert"><CircleAlert size={22} /> {serverError}</div>}
              <div className="kaizen-form-actions"><button type="button" className="kaizen-secondary-button" onClick={() => { setErrors({}); setStep(1); }}><ArrowLeft size={23} /> {t.backButton}</button><button type="submit" className="kaizen-primary-button" disabled={submitting}>{submitting ? <><Loader2 className="kaizen-spin" size={23} /> {t.submitting}</> : <><Send size={23} /> {t.submitButton}</>}</button></div>
            </>
          )}
        </form>

        <p className="kaizen-privacy-note"><ShieldCheck size={18} /> {t.privacy}</p>
      </section>
    </main>
  );
}

export default KaizenPublicForm;
